import { createAction } from '@reduxjs/toolkit';
import { ICalendarItemData } from 'Calendar/types';
import { IApiStaffByAppointmentParams, IApiStaffByAppointmentResData, IAppointmentSummaryCallerIdParams, ICalendarDropData, IStateCalendar } from 'services/types/apt';
import { IBlockHourResItem, IBodyAddBlockHours } from 'services/types/blockHours';
import { IApiHistoryCallerIdParams, ICallerIdHistoryResData, IPendingCallerResItem, ICustomerRecentApptsResData, ICustomerApptsPageParams, ICustomerApptsPageResData } from 'services/types/caller';
import { IBodyApiQuickBooking, IQuickBookingServiceItem } from 'services/types/quickBooking';
import { IAppointmentListParams, IPayloadBooking, IPayloadBookingV2 } from 'services/types/request';
import { IAptListResPayload } from 'services/types/response';
import { createAsyncAction } from 'store/actionConfigs';

const PREFIX_ACTIONS = 'appointment_feature_';

/**
 * `repeat` chi co khi nhan vien chu dong chon chu ky. PIN di kem vi tao chuoi la thao tac
 * ghi lon (toi 12 lich), cung muc voi update/delete/cancel.
 */
export type IQuickBookingPayload = {
  items: IBodyApiQuickBooking[];
  repeat?: { dayRepeat: number; pinCode: string };
  callerRequestId?: string; // set by the Caller v2 composer → saga reports the outcome back
};

const calendar = {
  setDistanceTimeline: createAction<IStateCalendar['distanceTimeLine']>(PREFIX_ACTIONS + '_configs_' + 'setDistanceTimeline'),
  setTypeView: createAction<IStateCalendar['typeView']>(PREFIX_ACTIONS + '_calendar_' + 'setTypeView'),
  setDateRanges: createAction<IStateCalendar['dateRanges']>(PREFIX_ACTIONS + '_calendar_' + 'setDateRanges'),
  updateDrop: createAsyncAction<ICalendarDropData>(PREFIX_ACTIONS + '_calendar_' + 'updateDrop'),
  getData: createAsyncAction<{ shopId?: string, startTime: string }>(PREFIX_ACTIONS + '_calendar_' + 'getData'),
  prolongedTime: createAction<{ id: string, status: 'ADD' | 'MINUS', pinCode: string }>(PREFIX_ACTIONS + '_calendar_' + 'prolongedTime'),
  refresh: createAction(PREFIX_ACTIONS + '_calendar_' + 'refresh'),
  setBlockHoursData: createAction<ICalendarItemData[]>(PREFIX_ACTIONS + 'setBlockHoursData'),
};

const table = {
  getData: createAsyncAction<IAppointmentListParams, IAptListResPayload>(PREFIX_ACTIONS + '_table_' + 'getData'),
  blockHours: createAsyncAction<IAppointmentListParams, IBlockHourResItem[]>(PREFIX_ACTIONS + '_table_' + 'getBlockHours'),
  setParams: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_table_' + 'setParams'),
};

const customer = {
  getData: createAsyncAction<IAppointmentListParams, IAptListResPayload>(PREFIX_ACTIONS + '_customer_' + 'getData'),
  setParams: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_customer_' + 'setParams'),
  initData: createAction<IAppointmentListParams>(PREFIX_ACTIONS + '_customer_' + 'initData'),
  clear: createAction(PREFIX_ACTIONS + '_customer_' + 'clear'),
};


const initData = createAction<string>(PREFIX_ACTIONS + 'initData');

const bookApt = createAction<IPayloadBooking>(PREFIX_ACTIONS + 'bookApt');

const bookAptV2 = createAction<IPayloadBookingV2>(PREFIX_ACTIONS + 'bookAptV2');

const deleteApt = createAction<{ aptId: string, pinCode: string, applyScope?: 'SINGLE' | 'SERIES' }>(PREFIX_ACTIONS + 'deleteApt');
const repeatApt = createAction<{ appointmentId: string | number, dayRepeat: number, pinCode: string }>(PREFIX_ACTIONS + 'repeatApt');

const cancelApt = createAction<{ aptId: string, reason: string, pinCode: string, applyScope?: 'SINGLE' | 'SERIES' }>(PREFIX_ACTIONS + 'cancelApt');

const confirmApt = createAction<{ appointmentId: string, pinCode: string }>(PREFIX_ACTIONS + 'confirmApt');

const changeAptStatus = createAction<{ appointmentId: string, status: number, pinCode: string }>(PREFIX_ACTIONS + 'changeAptStatus');

const checkInApt = createAction<string>(PREFIX_ACTIONS + 'checkInApt');

const updateBlockHour = createAction<IBodyAddBlockHours>(PREFIX_ACTIONS + 'updateBlockHour');

const addBlockHour = createAction<IBodyAddBlockHours>(PREFIX_ACTIONS + 'addBlockHour');

const deleteBlockHour = createAction<string>(PREFIX_ACTIONS + 'deleteBlockHour');
const onFormDeleteBlockHourSuccess = createAction<string>(PREFIX_ACTIONS + 'onFormDeleteBlockHourSuccess');

const prolongedTimeBlockHour = createAction<{ id: string, status: 'ADD' | 'MINUS' }>(PREFIX_ACTIONS + 'prolongedTimeBlockHour');

const updateApt = createAction<IPayloadBooking>(PREFIX_ACTIONS + 'updateApt');

const updateAptV2 = createAction<IPayloadBookingV2>(PREFIX_ACTIONS + 'updateAptV2');

const quickBooking = {
  getServices: createAsyncAction<string, IQuickBookingServiceItem[]>(PREFIX_ACTIONS + 'Quick-Booking' + 'getServices'),
  doQuickBooking: createAction<IQuickBookingPayload>(PREFIX_ACTIONS + 'doQuickBooking'),
};

const staffAvailableBooking = createAsyncAction<{ startTime: string, endTime: string }>(PREFIX_ACTIONS + 'getStaffAvailableBooking');

const getStaffByAppointment = createAsyncAction<Partial<IApiStaffByAppointmentParams>, IApiStaffByAppointmentResData>(PREFIX_ACTIONS + 'getStaffByAppointment');

const getCallerIdHistory = createAsyncAction<IApiHistoryCallerIdParams, ICallerIdHistoryResData>(PREFIX_ACTIONS + 'getCallerIdHistory');
const getAppointmentSummaryCallerId = createAsyncAction<IAppointmentSummaryCallerIdParams>(PREFIX_ACTIONS + 'getAppointmentSummaryCallerId');

// Caller V2 slices. `closeIfEmpty` (from a book/SMS action) closes the panel when
// the refreshed pending list comes back empty.
const getPendingCallers = createAsyncAction<{ closeIfEmpty?: boolean } | undefined, IPendingCallerResItem[]>(PREFIX_ACTIONS + 'getPendingCallers');
const getCustomerRecent = createAsyncAction<number, ICustomerRecentApptsResData>(PREFIX_ACTIONS + 'getCustomerRecent');
// Success carries the customerId it was fetched for, so a late response for a
// previous caller is dropped instead of showing up in the next caller's history.
const getCustomerAppointments = createAsyncAction<ICustomerApptsPageParams, ICustomerApptsPageResData & { customerId: number }>(PREFIX_ACTIONS + 'getCustomerAppointments');
// Drops the caller customer's history once they were booked.
const clearCustomerHistory = createAction(PREFIX_ACTIONS + 'clearCustomerHistory');

const blockHourList = createAsyncAction<undefined, IBlockHourResItem[]>(PREFIX_ACTIONS + 'blockHourList');
const getShopSurveyInfo = createAsyncAction(PREFIX_ACTIONS + 'getShopSurveyInfo');
const getCustomerTransactions = createAsyncAction<number>(PREFIX_ACTIONS + 'getCustomerTransactions');

const aptActions = {
  initData,
  calendar,
  table,
  customer,
  bookApt,
  bookAptV2,
  deleteApt,
  repeatApt,
  updateApt,
  updateAptV2,
  addBlockHour,
  deleteBlockHour,
  updateBlockHour,
  prolongedTimeBlockHour,
  quickBooking,
  cancelApt,
  confirmApt,
  changeAptStatus,
  staffAvailableBooking,
  checkInApt,
  onFormDeleteBlockHourSuccess,
  getStaffByAppointment,
  getCallerIdHistory,
  getAppointmentSummaryCallerId,
  getPendingCallers,
  getCustomerRecent,
  getCustomerAppointments,
  clearCustomerHistory,
  blockHourList,
  getShopSurveyInfo,
  getCustomerTransactions
};

export default aptActions;

